// This is for landing pages with single product only
// The styling is slightly different but the functionality
// Remains the same
import '@albelli/arc-3/lib/price.css';
import '@albelli/arc-3/lib/forms.css';
import '@albelli/arc-3/lib/buttons.css';
import '@albelli/arc-3/lib/callout_label.css';
import '../../style/main.scss';
import '../../style/offer-block.scss';

import useVendor from '../../hooks/useVendor';

import CopyToClipboard from './copy-to-clipboard';
import EditorButton from './editor-button';
import Trans from '../translations/trans';
import DateFormat from '../date/dateFormat';
import Price from './price';
import { domainPrefix } from '../../config/domainPrefix';
import { getPromoLabelPrefix, getPrices, getUspMarkup } from '../../helpers/offerBlockHelper';
import currencyFormatter from '../../helpers/currencyFormatter';

type SingleOfferBlockProps = {
    promotion: any;
    promoCode: string;
    showPricing: boolean;
    showUsp: boolean;
    productPrices: any;
    category: string;
    campaignUuid: string;
    fastLoad: boolean;
    mainTitle: string;
    subTitle: string;
    key?: number | string;
};

const SingleOfferBlock = ({
    promotion,
    promoCode,
    showPricing,
    showUsp,
    productPrices,
    category,
    campaignUuid,
    fastLoad,
    mainTitle,
    subTitle,
}: SingleOfferBlockProps) => {
    const promoLabel = promotion.highestOffer ? promotion.highestOffer : '';
    const promoLabelPrefix = getPromoLabelPrefix(promotion.offers, promotion.highestOffer);
    const vendor = useVendor();
    const articleTypes = Object.keys(promotion.offers).join('-').toLowerCase();

    // currently the campaign manager gives us a s3 URL, if this is the case we rewrite this to the CDN URL
    // so we can use the resizer Lambda to get properly sized images for our SRCsets
    // This processing can be removed when the API is adapted to serve the CDN URL
    let promoImage: string;

    if (promotion.image.includes('amazonaws')) {
        const regEx = /\/content\/.*/g;
        const image = regEx.exec(promotion.image);
        const imgUrl = image && image[0];
        promoImage = `https://${domainPrefix}${vendor + imgUrl}`;
    } else {
        promoImage = promotion.image;
    }

    const getPromoLabelBlock = () => {
        const showLabel =
            (promotion.promoLabel || promoLabel) &&
            (promotion.offerType === 'percentage' || promotion.offerType === 'fixed_amount');

        return showLabel ? (
            <div className={`arc3-callout-label --category-${category}`}>
                <p className='arc3-typo--text-uppercase arc3-typo--font-bold'>
                    {promotion.promoLabel ? (
                        promotion.promoLabel
                    ) : (
                        <>
                            {promoLabelPrefix ? <Trans transKey={promoLabelPrefix} /> : ''} -
                            {promotion.offerType === 'percentage'
                                ? `${promoLabel}%`
                                : currencyFormatter(promoLabel, vendor)}
                        </>
                    )}
                </p>
            </div>
        ) : (
            ''
        );
    };

    return (
        <div
            className='single-offer-block offer-block arc3-padding arc3-no-padding-phone arc3-no-margin--top'
            data-articleTypes={articleTypes}
        >
            <div className='title-block arc3-margin--right-lger'>
                <h2
                    data-cy='MainTitle'
                    className='arc3-typo--text-center --text-align-desktop--left arc3-margin-desktop--left'
                >
                    {mainTitle}
                </h2>
                <p
                    data-cy='MainDescription'
                    className='arc3-typo--text-center --text-align-desktop--left arc3-text-center--medium arc3-padding--left'
                >
                    {subTitle}
                </p>
            </div>
            <div className='img-block offer-block__wrapper --top arc3-flex-wrapper'>
                <a href={promotion.url}>
                    <picture>
                        <source srcSet={`${promoImage}?d=540x441`} media='(min-width:780px)' />
                        <source srcSet={`${promoImage}?d=559x447`} media='(min-width: 575px)' />
                        <img
                            loading={fastLoad ? 'eager' : 'lazy'}
                            src={`${promoImage}?d=310x248`}
                            width='420'
                            height='310'
                        />
                    </picture>
                </a>
                {getPromoLabelBlock()}
            </div>

            <div className='content-block'>
                {showUsp ? getUspMarkup(promotion) : ''}

                <div className='arc3-flex-wrapper --flex-direction-row --align-items-baseline arc3-margin--left'>
                    {showPricing ? <Price prices={getPrices(promotion.offers)} /> : ''}
                    <div
                        className={`arc3-hide-for-large flex-grow-1 ${
                            showPricing ? 'arc3-margin--left-lg' : ''
                        } arc3-margin--right`}
                    >
                        <p className='arc3-no-margin--bottom'>
                            <Trans transKey='discountOverview.discountCode.title' />
                        </p>

                        <CopyToClipboard promocode={promoCode} />
                    </div>
                </div>
                <div className='arc3-flex-wrapper'>
                    <p className='arc3-hide-for-medium-down arc3-margin--left arc3-margin--right arc3-no-margin--bottom'>
                        <Trans transKey='discountOverview.discountCode.title' />
                    </p>
                    <div className='arc3-hide-for-medium-down'>
                        <CopyToClipboard promocode={promoCode} />
                    </div>
                </div>
                <div className='cta offer-block__wrapper --bottom arc3-padding-phone arc3-padding--top arc3-padding--bottom'>
                    <EditorButton
                        promotionUrl={promotion.url}
                        articleTypes={promotion.offers}
                        campaignUuid={campaignUuid}
                    />
                </div>
                <div className='offer-block__title-wrapper arc3-margin--left'>
                    <small className='arc3-margin--bottom'>
                        {/* @TODO take offer extensions into account */}
                        <DateFormat
                            isoDateFormattedString={promotion.endDate}
                            dateFormatTransKey='promoBar.endDateFormat'
                        />
                    </small>
                </div>
            </div>
        </div>
    );
};

export default SingleOfferBlock;
